import {useEffect, useRef, useState} from "react";
import {dashboard} from "../config/config";
import {CChart} from "@coreui/react-chartjs";
import './wallboard.css'
import {
    CCard,
    CCardBody,
    CCardHeader,
    CCardTitle,
    CCol,
    CContainer,
    CRow,
    CFormSelect,
    CAccordion, CAccordionBody, CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter, CButton
} from "@coreui/react-pro";

function Wallboard({ lob = 'CCC', agent, onLOBChange, modalVisible, setModalVisible, setSelectedLOB, selectedLOB }) {
    const token = localStorage.getItem('authToken');
    const [metrics, setMetrics] = useState({});
    const [lobData, setLobData] = useState(null);
    const [debugMode, setDebugMode] = useState(false);
    // useEffect(() => {
    //     setModalVisible(modalVisible); // visible now comes from props
    // }, [modalVisible]);
    const websocket = useRef(null);

    // Cookie functions for LOB persistence
    const getCookie = (name) => {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop().split(';').shift();
        return null;
    };

    const setCookie = (name, value, days = 30) => {
        const expires = new Date();
        expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
        document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
    };

    // Get initial LOB from cookie or default
    const getInitialLOB = () => {
        const savedLOB = getCookie('selectedLOB');
        return savedLOB || lob || 'CCC';
    };

    // Update cookie when LOB changes
    const handleLOBChange = (newLOB) => {
        setSelectedLOB(newLOB);
        setCookie('selectedLOB', newLOB);
        console.log(`LOB changed to: ${newLOB}, saved to cookie`);
        setModalVisible(false);
        setSelectedLOB(newLOB);
    };

    useEffect(() => {
        function connect() {
            if (websocket.current) {
                websocket.current.close();
            }
            websocket.current = new WebSocket(`${dashboard}?token=Bearer ${token}&username=${agent.username}`);

            websocket.current.onopen = () => {
                console.log('WebSocket Connected');
            };

            websocket.current.onclose = event => {
                console.log('Dashboard WebSocket Disconnected. Attempting to reconnect...', event.reason);
                setTimeout(() => {
                    setMetrics({});
                    setLobData(null);
                    connect();
                }, 1000);
            };

            websocket.current.onmessage = event => {
                const newMessage = JSON.parse(event.data);
                console.log("New metrics", newMessage);
                setMetrics(newMessage);

                if (newMessage.LOBData) {
                    setLobData(newMessage.LOBData);

                    // Auto-adjust selected LOB if it doesn't exist in the data
                    const availableLOBs = Object.keys(newMessage.LOBData);
                    if (!availableLOBs.includes(selectedLOB) && availableLOBs.length > 0) {
                        const fallbackLOB = availableLOBs.includes('CCC') ? 'CCC' : availableLOBs[0];
                        console.log(`Selected LOB "${selectedLOB}" not available, switching to "${fallbackLOB}"`);
                        handleLOBChange(fallbackLOB);
                    }
                }
            };

            websocket.current.onerror = error => {
                console.error('WebSocket Error:', error);
                setMetrics({});
                setLobData(null);
            };
        }

        connect();

        return () => {
            if (websocket.current) {
                websocket.current.close();
            }
        };
    }, []);

    // Utility function for time formatting
    function secondsToMS(seconds) {
        const minutes = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        const formattedMinutes = String(minutes).padStart(2, '0');
        const formattedSeconds = String(secs).padStart(2, '0');
        return `${formattedMinutes}:${formattedSeconds}`;
    }

    // Enhanced metric card component that uses config for styling
    function MetricCard({ title, value, headerClass = null, formatter = null, config = null }) {
        const defaultHeaderClass = config?.metrics_to_display?.card_colors?.primary || "bg-new-dark-blue";
        const displayValue = formatter ? formatter(value) : (value || 0);

        return (
            <CCard className="text-center">
                <CCardHeader className={headerClass || defaultHeaderClass}>{title}</CCardHeader>
                <CCardBody>
                    <CCardTitle>{displayValue}</CCardTitle>
                </CCardBody>
            </CCard>
        );
    }

    // Config-driven metric card that automatically builds titles and styling
    function ConfigDrivenMetricCard({
                                        baseTitle,
                                        value,
                                        metricType,
                                        config,
                                        isPrimary = true,
                                        formatter = null
                                    }) {
        const displayNames = config?.display_names || {};
        const metricsDisplay = config?.metrics_to_display || {};
        const cardColors = metricsDisplay.card_colors || {};

        // Build display title
        let displayTitle = baseTitle;
        if (metricType && displayNames.metrics?.[metricType]) {
            const metricSuffix = displayNames.metrics[metricType];
            displayTitle = baseTitle ? `${baseTitle} ${metricSuffix}` : metricSuffix;
        }

        // Determine header class
        const headerClass = isPrimary ?
            (cardColors.primary || "bg-new-dark-blue") :
            (cardColors.secondary || "bg-new-light-blue");

        return (
            <MetricCard
                title={displayTitle}
                value={value}
                headerClass={headerClass}
                formatter={formatter}
                config={config}
            />
        );
    }

    // Enhanced LOB Selector Component with dropdown
    function LOBSelector({ lobData, currentLOB, onLOBChange }) {
        if (!lobData || Object.keys(lobData).length === 0) return null;

        const availableLOBs = Object.keys(lobData).map(lobKey => ({
            key: lobKey,
            name: lobData[lobKey].metadata?.lob_name || lobKey,
            type: lobData[lobKey].metadata?.type || 'unknown'
        }));

        // Sort LOBs to put CCC first if available
        const sortedLOBs = availableLOBs.sort((a, b) => {
            if (a.key === 'CCC') return -1;
            if (b.key === 'CCC') return 1;
            return a.name.localeCompare(b.name);
        });

        return (
            <>
                <CModal
                    visible={modalVisible}
                    onClose={() => setModalVisible(false)}
                >
                    <CModalHeader>
                        <CModalTitle>Change Line of Business</CModalTitle>
                    </CModalHeader>
                    <CModalBody>
                        <CRow>
                            <CFormSelect
                                value={currentLOB}
                                onChange={(e) => onLOBChange(e.target.value)}
                                // size="lg"
                            >
                                {sortedLOBs.map(lobInfo => (
                                    <option key={lobInfo.key} value={lobInfo.key}>
                                        {lobInfo.name.replace("CCC", "Customer Care Center").toUpperCase()}
                                    </option>
                                ))}
                            </CFormSelect>
                        </CRow>
                    </CModalBody>
                    <CModalFooter>
                        <CButton onClick={() => setModalVisible(false)} color="secondary">Close</CButton>
                    </CModalFooter>
                </CModal>
            </>

            // <CRow className="mb-3">
            //     <CCol md={12}>
            //         <CCard>
            //             <CCardBody>
            //                 <CRow className="align-items-center">
            //                     <CCol md={3}>
            //                         <h5 className="mb-0">Line of Business:</h5>
            //                     </CCol>
            //                     <CCol md={6}>
            //                         <CFormSelect
            //                             value={currentLOB}
            //                             onChange={(e) => onLOBChange(e.target.value)}
            //                             size="lg"
            //                         >
            //                             {sortedLOBs.map(lobInfo => (
            //                                 <option key={lobInfo.key} value={lobInfo.key}>
            //                                     {lobInfo.name} ({lobInfo.type.replace('_', ' ')})
            //                                 </option>
            //                             ))}
            //                         </CFormSelect>
            //                     </CCol>
            //                     <CCol md={3} className="text-right">
            //                         <small className="text-muted">
            //                             Saved to preferences
            //                         </small>
            //                     </CCol>
            //                 </CRow>
            //             </CCardBody>
            //         </CCard>
            //     </CCol>
            // </CRow>
        );
    }

    // Render language-category matrix dynamically based on config
    function renderLanguageCategoryMatrix(lobInfo) {
        const { metadata, data } = lobInfo;
        const languages = Object.keys(metadata.languages || {});
        const categories = Object.keys(metadata.categories || {});
        const displayNames = metadata.display_names || {};

        console.log('Matrix render - Languages:', languages, 'Categories:', categories);
        console.log('Language totals:', data.language_totals);
        console.log('Category totals:', data.category_totals);

        if (languages.length === 0 && categories.length === 0) {
            return (
                <CRow>
                    <CCol md={12}>
                        <CCard>
                            <CCardBody className="text-center">
                                <h5>No language or category data available for {metadata.lob_name}</h5>
                                <p>Check your configuration or data processing.</p>
                            </CCardBody>
                        </CCard>
                    </CCol>
                </CRow>
            );
        }

        return (
            <CRow>
                {/* Language columns - dynamically generated */}
                {languages.map(langCode => {
                    const langDisplayName = displayNames.languages?.[langCode] ||
                        metadata.languages[langCode]?.display_name ||
                        langCode;
                    const langData = data.language_totals?.[langCode] || {};

                    return (
                        <CCol md={3} sm={6} key={`lang-${langCode}`}>
                            <ConfigDrivenMetricCard
                                baseTitle={langDisplayName}
                                value={langData.staffed || 0}
                                metricType="staffed"
                                config={metadata}
                                isPrimary={true}
                            />
                            <br/>
                            <CRow>
                                <CCol md={6} xs={6} sm={6} className="remove-left-padding">
                                    <ConfigDrivenMetricCard
                                        baseTitle={langDisplayName}
                                        value={langData.queue_count || 0}
                                        metricType="queue"
                                        config={metadata}
                                        isPrimary={false}
                                    />
                                </CCol>
                                <CCol md={6} xs={6} sm={6} className="remove-right-padding">
                                    <ConfigDrivenMetricCard
                                        baseTitle={langDisplayName}
                                        value={langData.oldest_contact || 0}
                                        metricType="oldest_contact"
                                        config={metadata}
                                        isPrimary={false}
                                        formatter={secondsToMS}
                                    />
                                </CCol>
                            </CRow>
                            <br className="visible-xs visible-sm"/>
                        </CCol>
                    );
                })}

                {/* Category columns - dynamically generated */}
                {categories.map(catCode => {
                    const catDisplayName = displayNames.categories?.[catCode] ||
                        metadata.categories[catCode]?.display_name ||
                        catCode;
                    const catData = data.category_totals?.[catCode] || {};

                    return (
                        <CCol md={3} sm={6} key={`cat-${catCode}`}>
                            <ConfigDrivenMetricCard
                                baseTitle={catDisplayName}
                                value={catData.staffed || 0}
                                metricType="staffed"
                                config={metadata}
                                isPrimary={true}
                            />
                            <br/>
                            <CRow>
                                <CCol md={6} xs={6} sm={6} className="remove-left-padding">
                                    <ConfigDrivenMetricCard
                                        baseTitle={catDisplayName}
                                        value={catData.queue_count || 0}
                                        metricType="queue"
                                        config={metadata}
                                        isPrimary={false}
                                    />
                                </CCol>
                                <CCol md={6} xs={6} sm={6} className="remove-right-padding">
                                    <ConfigDrivenMetricCard
                                        baseTitle={catDisplayName}
                                        value={catData.oldest_contact || 0}
                                        metricType="oldest_contact"
                                        config={metadata}
                                        isPrimary={false}
                                        formatter={secondsToMS}
                                    />
                                </CCol>
                            </CRow>
                            <br className="visible-xs visible-sm"/>
                        </CCol>
                    );
                })}
            </CRow>
        );
    }

    // Render category-based view dynamically
    function renderCategoryBased(lobInfo) {
        const { metadata, data } = lobInfo;
        const categories = Object.keys(metadata.categories || {});
        const displayNames = metadata.display_names || {};

        console.log('Category-based render - Categories:', categories);
        console.log('Category data:', data.categories);

        if (categories.length === 0) {
            return (
                <CRow>
                    <CCol md={12}>
                        <CCard>
                            <CCardBody className="text-center">
                                <h5>No category data available for {metadata.lob_name}</h5>
                                <p>Check your configuration or data processing.</p>
                            </CCardBody>
                        </CCard>
                    </CCol>
                </CRow>
            );
        }

        return (
            <CRow>
                {categories.map(catCode => {
                    const catDisplayName = displayNames.categories?.[catCode] ||
                        metadata.categories[catCode]?.display_name ||
                        catCode;
                    const catData = data.categories?.[catCode] || {};

                    return (
                        <CCol md={3} sm={6} key={`category-${catCode}`}>
                            <ConfigDrivenMetricCard
                                baseTitle={catDisplayName}
                                value={catData.staffed || 0}
                                metricType="staffed"
                                config={metadata}
                                isPrimary={true}
                            />
                            <br/>
                            <CRow>
                                <CCol md={6} xs={6} sm={6} className="remove-left-padding">
                                    <ConfigDrivenMetricCard
                                        baseTitle={catDisplayName}
                                        value={catData.contacts_in_queue || 0}
                                        metricType="queue"
                                        config={metadata}
                                        isPrimary={false}
                                    />
                                </CCol>
                                <CCol md={6} xs={6} sm={6} className="remove-right-padding">
                                    <ConfigDrivenMetricCard
                                        baseTitle={catDisplayName}
                                        value={catData.oldest_contact_age || 0}
                                        metricType="oldest_contact"
                                        config={metadata}
                                        isPrimary={false}
                                        formatter={secondsToMS}
                                    />
                                </CCol>
                            </CRow>
                            <br className="visible-xs visible-sm"/>
                        </CCol>
                    );
                })}
            </CRow>
        );
    }

    // Render simple LOB view (fallback)
    function renderSimple(lobInfo) {
        const { metadata, data } = lobInfo;

        return (
            <CRow>
                <CCol md={4}>
                    <ConfigDrivenMetricCard
                        baseTitle="Total"
                        value={data.totals?.staffed || 0}
                        metricType="staffed"
                        config={metadata}
                        isPrimary={true}
                    />
                </CCol>
                <CCol md={4}>
                    <ConfigDrivenMetricCard
                        baseTitle="Total"
                        value={data.totals?.contacts_in_queue || 0}
                        metricType="queue"
                        config={metadata}
                        isPrimary={false}
                    />
                </CCol>
                <CCol md={4}>
                    <ConfigDrivenMetricCard
                        baseTitle="Total"
                        value={data.totals?.oldest_contact_age || 0}
                        metricType="oldest_contact"
                        config={metadata}
                        isPrimary={false}
                        formatter={secondsToMS}
                    />
                </CCol>
            </CRow>
        );
    }

    // Debug component to help troubleshoot data issues
    function DebugPanel({ lobInfo, selectedLOB }) {
        if (!debugMode || !lobInfo) return null;

        const { metadata, data } = lobInfo;

        return (
            <CRow className="mt-4">
                <CCol md={12}>
                    <CCard>
                        <CCardHeader className="bg-warning">
                            <h5>Debug Info (LOB: {selectedLOB})</h5>
                            <button
                                className="btn btn-sm btn-secondary float-right"
                                onClick={() => setDebugMode(false)}
                            >
                                Hide Debug
                            </button>
                        </CCardHeader>
                        <CCardBody>
                            <CRow>
                                <CCol md={4}>
                                    <h6>Metadata:</h6>
                                    <pre style={{fontSize: '10px', maxHeight: '400px', overflow: 'auto'}}>
                                        {JSON.stringify(metadata, null, 2)}
                                    </pre>
                                </CCol>
                                <CCol md={4}>
                                    <h6>Data Structure:</h6>
                                    <pre style={{fontSize: '10px', maxHeight: '400px', overflow: 'auto'}}>
                                        {JSON.stringify(data, null, 2)}
                                    </pre>
                                </CCol>
                                <CCol md={4}>
                                    <h6>Quick Stats:</h6>
                                    <ul style={{fontSize: '12px'}}>
                                        <li>LOB Type: {metadata.type}</li>
                                        <li>Configured Languages: {Object.keys(metadata.languages || {}).length}</li>
                                        <li>Configured Categories: {Object.keys(metadata.categories || {}).length}</li>
                                        <li>Languages with data: {Object.keys(data.language_totals || {}).length}</li>
                                        <li>Categories with data: {Object.keys(data.category_totals || data.categories || {}).length}</li>
                                        <li>Selected LOB: {selectedLOB}</li>
                                        <li>Cookie Value: {getCookie('selectedLOB') || 'Not set'}</li>
                                    </ul>
                                </CCol>
                            </CRow>
                        </CCardBody>
                    </CCard>
                </CCol>
            </CRow>
        );
    }

    // Get current LOB data with enhanced structure
    const currentLobInfo = lobData?.[selectedLOB];

    if (!lobData) {
        return (
            <div id="wallbaordView">
                <div className="text-center">
                    <h3>Connecting to metrics feed...</h3>
                    {/*<p>Loading LOB: {selectedLOB}</p>*/}
                </div>
            </div>
        );
    }

    if (!currentLobInfo) {
        const availableLOBs = Object.keys(lobData);
        return (
            <div id="wallbaordView">
                <div className="text-center">
                    <h3>LOB "{selectedLOB}" not found</h3>
                    <p>Available LOBs: {availableLOBs.join(', ')}</p>
                    {availableLOBs.length > 0 && (
                        <p>
                            <button
                                className="btn btn-primary"
                                onClick={() => handleLOBChange(availableLOBs.includes('CCC') ? 'CCC' : availableLOBs[0])}
                            >
                                Switch to {availableLOBs.includes('CCC') ? 'CCC' : availableLOBs[0]}
                            </button>
                        </p>
                    )}
                </div>
            </div>
        );
    }

    const { metadata, data } = currentLobInfo;

    // Get total metrics based on LOB type
    const getTotalMetrics = () => {
        if (metadata.type === 'language_category_matrix') {
            return data.grand_total || {};
        } else if (metadata.type === 'category_based') {
            return data.totals || {};
        } else {
            return data.totals || {};
        }
    };

    const totalMetrics = getTotalMetrics();
    const channelMetrics = data.channel_metrics[lob] || {};

    return (
        <div id="wallbaordView">
            {/* Enhanced LOB Selector with dropdown */}
            <LOBSelector
                lobData={lobData}
                currentLOB={selectedLOB}
                onLOBChange={handleLOBChange}
            />

             {/*Debug toggle button */}
            {/*<div className="text-right mb-2">*/}
            {/*    <button*/}
            {/*        className="btn btn-sm btn-outline-secondary"*/}
            {/*        onClick={() => setDebugMode(!debugMode)}*/}
            {/*    >*/}
            {/*        {debugMode ? 'Hide' : 'Show'} Debug*/}
            {/*    </button>*/}
            {/*</div>*/}

            {/* Dynamic metrics based on LOB type - completely config-driven */}
            {metadata.type === 'language_category_matrix' && renderLanguageCategoryMatrix(currentLobInfo)}
            {metadata.type === 'category_based' && renderCategoryBased(currentLobInfo)}
            {metadata.type === 'simple' && renderSimple(currentLobInfo)}

            <br/>

            {/* Common bottom section - now using config for display names */}
            <CRow className="align-items-center">
                <CCol md={3}>
                    <ConfigDrivenMetricCard
                        baseTitle=""
                        value={totalMetrics.queue_count || totalMetrics.contacts_in_queue || channelMetrics.CONTACTS_IN_QUEUE || 0}
                        metricType="call_queue"
                        config={metadata}
                        isPrimary={false}
                    />
                    <br/>
                    <ConfigDrivenMetricCard
                        baseTitle=""
                        value={totalMetrics.oldest_contact || totalMetrics.oldest_contact_age || channelMetrics.OLDEST_CONTACT_AGE || 0}
                        metricType="oldest_call"
                        config={metadata}
                        isPrimary={false}
                        formatter={secondsToMS}
                    />
                </CCol>

                <CCol md={5} className="text-center">
                    <br className="visible-xs visible-sm"/>
                    <h3 className="heading">Agent Status</h3>
                    <br/>
                    <CChart
                        type="pie"
                        data={{
                            labels: ['Available', 'On Contact', 'Online', 'Other'],
                            datasets: [{
                                backgroundColor: ['#001689', '#FED600', '#4C9E45', '#009BDA'],
                                data: [
                                    channelMetrics.AGENTS_AVAILABLE || 0,
                                    channelMetrics.AGENTS_ON_CALL || 0,
                                    channelMetrics.AGENTS_ONLINE || 0,
                                    (channelMetrics.AGENTS_AFTER_CONTACT_WORK || 0) +
                                    (channelMetrics.AGENTS_NON_PRODUCTIVE || 0) +
                                    (channelMetrics.AGENTS_ERROR || 0)
                                ],
                            }],
                        }}
                        options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            radius: "100%",
                            plugins: {
                                legend: {
                                    position: "top",
                                }
                            },
                            layout: {
                                padding: {
                                    top: 0,
                                    bottom: 0,
                                    left: 0,
                                    right: 0
                                }
                            },
                        }}
                        style={{
                            height: "500px",
                            maxHeight: "100%",
                            width: "100%",
                        }}
                    />
                </CCol>

                <CCol md={4}>
                    <CCard>
                        <br/>
                        <CCardTitle className="heading">Agent Status</CCardTitle>
                        <CCardBody id="legend">
                            <CRow>
                                <CCol md={4}>
                                    <span className="legendKey">Available</span>
                                </CCol>
                                <CCol md={8} className="remove-left-padding">
                                    <span>The number of agents who can take an inbound contact</span>
                                </CCol>
                            </CRow><br/>
                            <CRow>
                                <CCol md={4}>
                                    <span className="legendKey">On Contact</span>
                                </CCol>
                                <CCol md={8} className="remove-left-padding">
                                    <span>The number of agents currently on a contact (connected, on hold, in ACW, paused or in outbound ring state)</span>
                                </CCol>
                            </CRow><br/>
                            <CRow>
                                <CCol md={4}>
                                    <span className="legendKey">Online</span>
                                </CCol>
                                <CCol md={8} className="remove-left-padding">
                                    <span>The number of agents who have set their status to something other than offline</span>
                                </CCol>
                            </CRow><br/>
                            <CRow>
                                <CCol md={4}>
                                    <span className="legendKey">Other</span>
                                </CCol>
                                <CCol md={8} className="remove-left-padding">
                                    <span>Agents in custom status, or error state</span>
                                </CCol>
                            </CRow>
                        </CCardBody>
                    </CCard>
                </CCol>
            </CRow>

            {/* Debug Panel */}
            <DebugPanel lobInfo={currentLobInfo} selectedLOB={selectedLOB} />
        </div>
    );
}

export default Wallboard;